import React, { useEffect, useState } from "react";
import FormControl, { TextFieldControl } from "components/FormControl";
import { Column, Row, ControlsContainer, ControlContainer } from "./styles";
import SubmitButtonControl, {
  CancelButtonControl,
} from "components/FormControl/SubmitButtonControl";
import RestrictionStore from "stores/RestrictionStore";
import LoadingButton from "components/LoadingButton/LoadingButton";
import { Button } from "@material-ui/core";
import { FormSpy } from "react-final-form";
import Popup from "components/Popup/Popup";
import ConfirmationDialog from "components/ConfirmationDialog/ConfirmationDialog";
import CollectionStore, { CollectionModel } from "stores/CollectionStore";
import { SNACKBAR_TYPES } from "components/Snackbar/Snackbar";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";
import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";
import { getMessage } from "utils/messages";
import { Instance } from "mobx-state-tree";

interface AddEditFormProps extends GlobalPopupProps, SnackbarContextProps {
  initialValues: Instance<typeof CollectionModel> | undefined;
  isEditMode: boolean;
  isNewItem: boolean;
  wantsGoBack: boolean;
  onBack: () => void;
  setMessage: (message: string, type?: string) => void;
}

const AddEditForm = ({
  initialValues,
  isEditMode,
  isNewItem,
  wantsGoBack,
  onBack,
  setMessage,
}: AddEditFormProps) => {
  const [updateConfirmationIsOpened, setUpdateConfirmationIsOpened] = useState(
    false,
  );
  const [closeConfirmationIsOpened, setCloseConfirmationIsOpened] = useState(
    false,
  );
  const [isPopupDirty, setIsPopupDirty] = useState(false);

  useEffect(() => {
    wantsGoBack && onCancel();
  }, [wantsGoBack]);

  const renderCancelButton = ({ onClick }) => (
    <Button variant="outlined" color="primary" onClick={onClick} fullWidth>
      Cancel
    </Button>
  );

  const renderButton = ({ onClick }) => (
    <LoadingButton
      onClick={onClick}
      variant="contained"
      color="primary"
      isLoading={RestrictionStore.isManaging}
      fullWidth
    >
      {isNewItem ? "Add" : "Save"}
    </LoadingButton>
  );

  const getResources = (values: any) => {
    const resources = [];
    if (values.resourceUrlEnglish || values.resourceUrlSpanish) {
      const englishR = values.resourceUrlEnglish && {
        localeId: "en-US",
        name: values.buttonEnglish,
        url: values.resourceUrlEnglish,
      };
      const spanishR = values.resourceUrlSpanish && {
        localeId: "es-ES",
        name: values.buttonSpanish,
        url: values.resourceUrlSpanish,
      };
      englishR && resources.push(englishR);
      spanishR && resources.push(spanishR);
    }
    return resources;
  };
  const updateCollectionValues = async (values: any, id: number) => {
    try {
      values.resources = getResources(values);
      await CollectionStore.updateCollection(values, id, undefined);
      setMessage(
        "Special Program updated successfully",
        SNACKBAR_TYPES.SUCCESS,
      );
      onBack();
    } catch (error) {
      setMessage(getMessage(error?.message), SNACKBAR_TYPES.ERROR);
    }
  };

  const createCollectionValues = async (values: any) => {
    try {
      values.resources = getResources(values);
      await CollectionStore.createCollection(values);
      setMessage(
        "Special Program created successfully",
        SNACKBAR_TYPES.SUCCESS,
      );
      onBack();
    } catch (error) {
      setMessage(getMessage(error?.message), SNACKBAR_TYPES.ERROR);
    }
  };

  const onCancel = () => {
    if (isPopupDirty) {
      return setCloseConfirmationIsOpened(true);
    }
    onBack();
  };

  const onChangeInput = ({
    values,
  }: {
    values: Instance<typeof CollectionModel>;
  }) => {
    if (
      Object.keys(values).length > 0 &&
      JSON.stringify(initialValues) !== JSON.stringify(values)
    ) {
      setIsPopupDirty(true);
    }
  };

  const closeUpdateConfirmation = () => {
    setUpdateConfirmationIsOpened(false);
  };

  const openUpdateConfirmation = () => {
    setUpdateConfirmationIsOpened(true);
  };

  const confirmUpdate = values => () => {
    closeUpdateConfirmation();
    submit(values);
  };

  const onSubmit = () => {
    if (isPopupDirty) {
      openUpdateConfirmation();
    }
  };

  const submit = async (values: any) => {
    isEditMode && initialValues?.id
      ? updateCollectionValues(values, initialValues.id)
      : createCollectionValues(values);
  };
  const validate = (values: any) => {
    const errors: { [fieldName: string]: string } = {};

    if (values.resourceUrlEnglish && !values.buttonEnglish) {
      errors.buttonEnglish =
        "This field is required when Resource URL English is filled";
    }

    if (!values.resourceUrlEnglish && values.buttonEnglish) {
      errors.resourceUrlEnglish =
        "This field is required when Button English is filled";
    }

    if (values.resourceUrlSpanish && !values.buttonSpanish) {
      errors.buttonSpanish =
        "This field is required when Resource URL Spanish is filled";
    }

    if (!values.resourceUrlSpanish && values.buttonSpanish) {
      errors.resourceUrlSpanish =
        "This field is required when Button Spanish is filled";
    }

    return errors;
  };
  const closeConfirmation = () => {
    closeConfirmationPopup();
    onBack();
  };

  const closeConfirmationPopup = () => {
    setCloseConfirmationIsOpened(false);
  };

  return (
    <>
      {closeConfirmationIsOpened && (
        <Popup onClose={closeConfirmationPopup} withoutExitButton>
          <ConfirmationDialog
            title="Discard Changes"
            text="Are you sure you would like to discard changes? All information will be lost."
            declineText="Cancel"
            acceptText="Discard"
            onDecline={closeConfirmationPopup}
            onAccept={closeConfirmation}
          />
        </Popup>
      )}
      <FormControl
        onSubmit={onSubmit}
        initialValues={initialValues}
        keepDirtyOnReinitialize={isNewItem}
        validate={validate}
      >
        {updateConfirmationIsOpened && (
          <FormSpy subscription={{ values: true }}>
            {({ values }) => (
              <Popup onClose={closeUpdateConfirmation} withoutExitButton>
                <ConfirmationDialog
                  title="Save Changes"
                  text="Are you sure you want to proceed?"
                  acceptText="Proceed"
                  declineText="Cancel"
                  onAccept={confirmUpdate(values)}
                  onDecline={closeUpdateConfirmation}
                />
              </Popup>
            )}
          </FormSpy>
        )}
        <FormSpy
          subscription={{ values: true, dirty: true }}
          onChange={onChangeInput}
        />
        <ControlsContainer>
          <ControlContainer>
            <CancelButtonControl
              render={renderCancelButton}
              onClick={onCancel}
            />
          </ControlContainer>
          <ControlContainer>
            <SubmitButtonControl render={renderButton} onClick={onSubmit} />
          </ControlContainer>
        </ControlsContainer>
        <Column width="100%">
          <Row width="100%">
            <TextFieldControl
              label="Name"
              name="name"
              variant="outlined"
              margin="none"
              required
            />
            <TextFieldControl
              label="Value"
              name="value"
              variant="outlined"
              margin="none"
              required
            />
          </Row>

          <Row>
            <TextFieldControl
              label="Resource URL - English"
              name="resourceUrlEnglish"
              variant="outlined"
              margin="none"
            />
            <TextFieldControl
              label="Resource URL - Spanish"
              name="resourceUrlSpanish"
              variant="outlined"
              margin="none"
            />
          </Row>
          <Row>
            <TextFieldControl
              label="Button Words - English"
              name="buttonEnglish"
              variant="outlined"
              margin="none"
            />
            <TextFieldControl
              label="Button Words - Spanish"
              name="buttonSpanish"
              variant="outlined"
              margin="none"
            />
          </Row>
        </Column>
      </FormControl>
    </>
  );
};

export default withGlobalPopup(withGlobalSnackbar(AddEditForm));
